'use client';

/**
 * FacetControl (BAL-665) — renders one facet as the right control for its kind:
 * - toggle   → segmented pill group (Button-variant membership; supports MULTI,
 *              unlike @kit/ui/toggle-group which is single-select/radiogroup)
 * - dropdown → popover with checkboxes + an applied count
 * - range    → dual-thumb slider with min/max labels
 */
import { ChevronDownIcon } from 'lucide-react';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Checkbox } from '@kit/ui/checkbox';
import { Label } from '@kit/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@kit/ui/popover';
import { Slider } from '@kit/ui/slider';
import { cn } from '@kit/ui/utils';

import type { FilterFacet } from './types';

interface FacetControlProps {
  facet: FilterFacet;
  /** Selected values (toggle/dropdown) or [min,max] (range). */
  values: string[];
  onToggle: (value: string) => void;
  onSetValues: (values: string[]) => void;
  /** Stack full-width (mobile sheet) vs inline (web bar). */
  block?: boolean;
}

export function FacetControl({
  facet,
  values,
  onToggle,
  onSetValues,
  block,
}: FacetControlProps) {
  if (facet.kind === 'toggle') {
    return (
      <div
        className={cn(
          'inline-flex flex-wrap gap-1',
          block && 'w-full',
        )}
        role="group"
        aria-label={facet.label}
      >
        {facet.options.map((opt) => {
          const active = values.includes(opt.value);
          return (
            <Button
              key={opt.value}
              type="button"
              size="sm"
              variant={active ? 'default' : 'outline'}
              aria-pressed={active}
              onClick={() => onToggle(opt.value)}
              className={cn(block && 'flex-1')}
            >
              {opt.label}
            </Button>
          );
        })}
      </div>
    );
  }

  if (facet.kind === 'dropdown') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn('justify-between gap-2', block && 'w-full')}
          >
            <span>{facet.label}</span>
            {values.length > 0 ? (
              <Badge variant="secondary" className="ml-1 px-1.5">
                {values.length}
              </Badge>
            ) : (
              <ChevronDownIcon className="size-4 opacity-60" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="start">
          <div className="flex flex-col gap-1">
            {facet.options.map((opt) => {
              const id = `${facet.key}-${opt.value}`;
              return (
                <label
                  key={opt.value}
                  htmlFor={id}
                  className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm"
                >
                  <Checkbox
                    id={id}
                    checked={values.includes(opt.value)}
                    onCheckedChange={() => onToggle(opt.value)}
                  />
                  {opt.label}
                </label>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // range
  const min = values[0] != null ? Number(values[0]) : facet.min;
  const max = values[1] != null ? Number(values[1]) : facet.max;
  return (
    <div className={cn('flex flex-col gap-2', block ? 'w-full' : 'w-56')}>
      <div className="flex items-center justify-between">
        <Label className="text-xs">{facet.label}</Label>
        <span className="text-muted-foreground text-xs tabular-nums">
          {min}–{max}
          {facet.unit ? ` ${facet.unit}` : ''}
        </span>
      </div>
      <Slider
        min={facet.min}
        max={facet.max}
        value={[min, max]}
        onValueChange={(next) => {
          const [lo, hi] = next;
          if (lo === facet.min && hi === facet.max) onSetValues([]);
          else onSetValues([String(lo), String(hi)]);
        }}
      />
    </div>
  );
}
